import { customerOrganizationService } from '@/services/CustomerOrganizationService';
import { FetchResult } from 'apollo-link';
import { ApolloQueryResult } from 'apollo-client';
import {
  CustomerOrganizationFields,
  PaginationFields,
  CustomerOrganizationInput,
  CustomerOrganizationHideInput,
  fetchCustomerOrganizations,
  fetchCustomerOrganizationsVariables,
  updateCustomerOrganization
} from '@/types/graphql/schemaTypes';
import { CustomerOrganizationState, RootState } from '@/types/store';
import { GetterTree, MutationTree, ActionTree, Module } from 'vuex';
import i18n from '@/plugins/i18n';

// initial state
const state: CustomerOrganizationState = {
  customerOrganization: null,
  customerOrganizations: [],
  totalCount: 0,
  upsertCustomerOrganization: null,
  refresh: false
};

const getters: GetterTree<CustomerOrganizationState, RootState> = {
  customerOrganizations(customerOrganizationState): CustomerOrganizationFields[] {
    return [...customerOrganizationState.customerOrganizations];
  },
  totalCount(customerOrganizationState): number {
    return customerOrganizationState.totalCount;
  },
  customerOrganization(customerOrganizationState): CustomerOrganizationFields | {} {
    return { ...customerOrganizationState.customerOrganization } || {};
  },
  refresh(customerOrganizationState): boolean {
    return customerOrganizationState.refresh;
  }
};

// actions
const actions: ActionTree<CustomerOrganizationState, RootState> = {
  async fetchCustomerOrganization({ commit }, customerOrganizationId: string): Promise<void> {
    customerOrganizationService.listCustomerOrganizations({
      customerOrganizationId
    }).then((response: ApolloQueryResult<fetchCustomerOrganizations>): void => {
      commit('setCustomerOrganization', response.data.customerOrganizations[0]);
    });
  },
  async fetchCustomerOrganizations(
    { commit },
    args: fetchCustomerOrganizationsVariables
  ): Promise<void> {
    customerOrganizationService.listCustomerOrganizations(
      args.customerOrganizationArgs,
      args.pagination,
      args.filter
    ).then((response: ApolloQueryResult<fetchCustomerOrganizations>): void => {
      commit('setCustomerOrganizations', response.data.customerOrganizations);
      commit('setPagination', response.data.customerOrganizationsPagination);
      commit('setRefresh', false);
    });
  },
  async updateCustomerOrganization(
    { commit },
    customerOrganizationArgs: CustomerOrganizationInput
  ): Promise<void> {
    customerOrganizationService.updateCustomerOrganization(customerOrganizationArgs)
      .then((response: FetchResult<updateCustomerOrganization>): void => {
        const data = response.data as updateCustomerOrganization;
        commit('setCustomerOrganization', data.updateCustomerOrganization);
      });
  },
  async hideCustomerOrganization(
    { commit },
    hideCustomerOrganizationArgs: CustomerOrganizationHideInput
  ) {
    customerOrganizationService.hideCustomerOrganization(hideCustomerOrganizationArgs)
      .then((response) => {
        if (response.data) {
          commit('setCustomerOrganization', (response.data as any).hideCustomerOrganization);
          commit('Alert/addAlert', {
            type: 'success',
            message: i18n.t('success.updated'),
            time: 3000
          }, { root: true });
        }
      }).catch(() => {
        commit('Alert/addAlert', {
          type: 'error',
          message: i18n.t('error.push')
        }, { root: true });
      });
  },
  async setRefresh({ commit }): Promise<void> {
    commit('setRefresh');
  }
};

// mutations
const mutations: MutationTree<CustomerOrganizationState> = {
  setCustomerOrganization(
    customerOrganizationState,
    customerOrganization: CustomerOrganizationFields
  ): void {
    customerOrganizationState.customerOrganization = customerOrganization;
  },
  setCustomerOrganizations(
    customerOrganizationState,
    customerOrganizations: CustomerOrganizationFields[]
  ): void {
    customerOrganizationState.customerOrganizations = customerOrganizations;
  },
  setPagination(customerOrganizationState, pagination: PaginationFields): void {
    customerOrganizationState.totalCount = pagination.totalCount;
  },
  setRefresh(customerOrganizationState, refresh: boolean) {
    customerOrganizationState.refresh = refresh;
  }
};

export const customerOrganization: Module<CustomerOrganizationState, RootState> = {
  state,
  getters,
  actions,
  mutations,
  namespaced: true
};
